/**
 * LicenseEditModal Component
 * 
 * Modal for editing or adding a physician's license for a specific state
 * Saves changes to draft store instead of directly to API
 *
 * The edit is merged into the state's existing license: fields the form
 * doesn't show (issue dates, incident2, anything else core stores) are kept.
 * Addresses use core's shape {line, city, state, zip}; an older record's
 * {street, zip_code} is read and rewritten in that shape.
 *
 * `draftStore` / `draftScope` let a page draft into another store than the
 * clinic's — the shared doctor directory passes its own.
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui'
import useDraftStore from '../stores/draftStore'
import { useAuthStore } from '../stores/authStore'

// US State codes for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'PR', name: 'Puerto Rico' }, { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' }
]

export default function LicenseEditModal({ 
  isOpen, 
  onClose, 
  physician,  // Full physician object
  stateCode = null, // null for adding new, state code for editing
  licenseData = null, // Existing license data for the state
  onSave,
  draftStore = useDraftStore,
  draftScope = null, // draft namespace; defaults to the selected clinic
}) {
  const [searchParams] = useSearchParams()
  const { user, currentClinicId } = useAuthStore()
  const clinicId = draftScope || currentClinicId || searchParams.get('clinic') || 'tmates'
  
  const isNewLicense = !stateCode
  
  const [formData, setFormData] = useState({
    stateCode: '',
    stateLicense: '',
    stateExpiry: '',
    csrLicense: '',
    csrExpiry: '',
    deaLicense: '',
    deaExpiry: '',
    address: {
      line: '',
      city: '',
      state: '',
      zip: ''
    }
  })
  
  const [isLoading, setIsLoading] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState({})
  
  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (stateCode && licenseData) {
        // Editing existing license
        setFormData({
          stateCode: stateCode,
          stateLicense: licenseData.state?.license || '',
          stateExpiry: licenseData.state?.expiry ? formatDateForInput(licenseData.state.expiry) : '',
          csrLicense: licenseData.CSR?.license || '',
          csrExpiry: licenseData.CSR?.expiry ? formatDateForInput(licenseData.CSR.expiry) : '',
          deaLicense: licenseData.DEA?.license || '',
          deaExpiry: licenseData.DEA?.expiry ? formatDateForInput(licenseData.DEA.expiry) : '',
          address: {
            line: licenseData.address?.line || licenseData.address?.street || '',
            city: licenseData.address?.city || '',
            state: licenseData.address?.state || stateCode,
            zip: licenseData.address?.zip || licenseData.address?.zip_code || ''
          }
        })
      } else {
        // New license - reset to defaults
        setFormData({
            stateCode: '',
            stateLicense: '',
            stateExpiry: '',
            csrLicense: '',
            csrExpiry: '',
            deaLicense: '',
            deaExpiry: '',
            address: {
              line: '',
              city: '',
              state: '',
              zip: ''
            }
          })
      }
      setHasChanges(false)
      setErrors({})
    }
  }, [isOpen, stateCode, licenseData])
  
  // Initialize draft store when modal opens
  useEffect(() => {
    if (isOpen && user && clinicId) {
      const { initialize, isInitialized, clinicId: storeClinicId, userId: storeUserId } = draftStore.getState()
      
      const userId = user.id
      
      if (!isInitialized || storeUserId !== userId || storeClinicId !== clinicId) {
        console.log('[LICENSE MODAL] Initializing draft store with:', { clinicId, userId })
        initialize(clinicId, userId)
      }
    }
  }, [isOpen, user, clinicId, draftStore])
  
  // Format date for input field (YYYY-MM-DD)
  function formatDateForInput(dateString) {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      // eslint-disable-next-line no-restricted-syntax -- server sends UTC midnight dates
      return date.toISOString().split('T')[0]
    } catch {
      return ''
    }
  }
  
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setHasChanges(true)
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }
  
  const handleAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }))
    setHasChanges(true)
  }
  
  const validateForm = () => {
    const newErrors = {}
    
    if (isNewLicense && !formData.stateCode) {
      newErrors.stateCode = 'Please select a state'
    }
    
    // At least one license (state, CSR or DEA) is required
    if (!formData.stateLicense.trim() && !formData.csrLicense.trim() && !formData.deaLicense.trim()) {
      newErrors.stateLicense = 'At least one license (State, CSR or DEA) is required'
    }
    
    // Validate expiry dates if licenses are provided
    if (formData.stateLicense.trim() && formData.stateExpiry) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(formData.stateExpiry)) {
        newErrors.stateExpiry = 'Invalid date format'
      }
    }
    
    if (formData.csrLicense.trim() && formData.csrExpiry) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(formData.csrExpiry)) {
        newErrors.csrExpiry = 'Invalid date format'
      }
    }
    
    if (formData.deaLicense.trim() && formData.deaExpiry) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(formData.deaExpiry)) {
        newErrors.deaExpiry = 'Invalid date format'
      }
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }
  
  const handleSave = async () => {
    if (!validateForm()) return
    
    setIsLoading(true)
    try {
      const { addChange } = draftStore.getState()
      
      const targetStateCode = isNewLicense ? formData.stateCode : stateCode
      
      // Merge into what the state already holds, so fields this form doesn't
      // edit (issue dates, incident2, ...) survive. The old address keys are
      // dropped in favour of core's {line, zip}.
      const existing = physician?.licenses?.[targetStateCode] || {}
      const { street, zip_code, ...existingAddress } = existing.address || {}
      const updatedLicenseData = {
        ...existing,
        state: {
          ...existing.state,
          license: formData.stateLicense.trim() || '',
          expiry: formData.stateExpiry || ''
        },
        CSR: {
          ...existing.CSR,
          license: formData.csrLicense.trim() || '',
          expiry: formData.csrExpiry || ''
        },
        DEA: {
          ...existing.DEA,
          license: formData.deaLicense.trim() || '',
          expiry: formData.deaExpiry || ''
        },
        address: {
          ...existingAddress,
          line: formData.address.line.trim(),
          city: formData.address.city.trim(),
          state: formData.address.state.trim() || targetStateCode,
          zip: formData.address.zip.trim()
        }
      }
      
      // Create the updated physician object with the new license data
      const updatedPhysician = {
        ...physician,
        licenses: {
          ...physician.licenses,
          [targetStateCode]: updatedLicenseData
        }
      }
      
      // Add change to draft store
      addChange({
        resource_type: 'physician',
        resource_id: physician._id || physician.id,
        operation: 'update',
        original_data: physician,
        new_data: updatedPhysician,
        description: `${isNewLicense ? 'Add' : 'Update'} license for ${targetStateCode}: ${physician.first_name} ${physician.last_name}`
      })
      
      console.log(`[DRAFT] Saved license ${isNewLicense ? 'addition' : 'update'} for ${targetStateCode}`)
      
      // Call onSave callback (for UI refresh)
      onSave?.(updatedPhysician)
      onClose()
      setHasChanges(false)
    } catch (error) {
      console.error('Failed to save license to drafts:', error)
      alert('Failed to save changes. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleClose = () => {
    if (hasChanges) {
      if (!confirm('You have unsaved changes. Are you sure you want to close?')) {
        return
      }
    }
    onClose()
  }
  
  // Get state name from code
  const getStateName = (code) => {
    return US_STATES.find(s => s.code === code)?.name || code
  }
  
  // Filter out states that already have licenses
  const availableStates = isNewLicense 
    ? US_STATES.filter(state => !physician?.licenses?.[state.code])
    : US_STATES
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNewLicense ? 'Add New License' : `Edit License - ${getStateName(stateCode)} (${stateCode})`}
          </DialogTitle>
          <DialogDescription>
            {isNewLicense 
              ? 'Add a new state license for this physician. Changes will be saved as a draft.' 
              : 'Edit the license information for this state. Changes will be saved as a draft.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* State Selection (only for new licenses) */}
          {isNewLicense && (
            <div className="space-y-2">
              <Label htmlFor="stateCode">State *</Label>
              <Select 
                value={formData.stateCode} 
                onValueChange={(value) => handleChange('stateCode', value)}
              >
                <SelectTrigger className={errors.stateCode ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select a state" />
                </SelectTrigger>
                <SelectContent>
                  {availableStates.map(state => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.name} ({state.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.stateCode && (
                <p className="text-sm text-destructive">{errors.stateCode}</p>
              )}
            </div>
          )}
          
          {/* State License Section */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold border-b pb-2">State Medical License</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="stateLicense">License Number</Label>
                <Input
                  id="stateLicense"
                  value={formData.stateLicense}
                  onChange={(e) => handleChange('stateLicense', e.target.value)}
                  placeholder="e.g., MD12345"
                  className={errors.stateLicense ? 'border-destructive' : ''}
                />
                {errors.stateLicense && (
                  <p className="text-sm text-destructive mt-1">{errors.stateLicense}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="stateExpiry">Expiry Date</Label>
                <Input
                  id="stateExpiry"
                  type="date"
                  value={formData.stateExpiry}
                  onChange={(e) => handleChange('stateExpiry', e.target.value)}
                  className={errors.stateExpiry ? 'border-destructive' : ''}
                />
                {errors.stateExpiry && (
                  <p className="text-sm text-destructive mt-1">{errors.stateExpiry}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* CSR Section */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold border-b pb-2">State Controlled Substance Registration (CSR)</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="csrLicense">CSR Number</Label>
                <Input
                  id="csrLicense"
                  value={formData.csrLicense}
                  onChange={(e) => handleChange('csrLicense', e.target.value)}
                  placeholder="State CSR number"
                />
              </div>
              
              <div>
                <Label htmlFor="csrExpiry">Expiry Date</Label>
                <Input
                  id="csrExpiry"
                  type="date"
                  value={formData.csrExpiry}
                  onChange={(e) => handleChange('csrExpiry', e.target.value)}
                  className={errors.csrExpiry ? 'border-destructive' : ''}
                />
                {errors.csrExpiry && (
                  <p className="text-sm text-destructive mt-1">{errors.csrExpiry}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* DEA License Section */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold border-b pb-2">DEA License</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="deaLicense">DEA Number</Label>
                <Input
                  id="deaLicense"
                  value={formData.deaLicense}
                  onChange={(e) => handleChange('deaLicense', e.target.value)}
                  placeholder="e.g., AB1234567"
                />
                {errors.deaLicense && (
                  <p className="text-sm text-destructive mt-1">{errors.deaLicense}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="deaExpiry">Expiry Date</Label>
                <Input
                  id="deaExpiry"
                  type="date"
                  value={formData.deaExpiry}
                  onChange={(e) => handleChange('deaExpiry', e.target.value)}
                  className={errors.deaExpiry ? 'border-destructive' : ''}
                />
                {errors.deaExpiry && (
                  <p className="text-sm text-destructive mt-1">{errors.deaExpiry}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Address Section */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold border-b pb-2">Practice Address (Optional)</h3>
            
            <div>
              <Label htmlFor="line">Street Address</Label>
              <Input
                id="line"
                value={formData.address.line}
                onChange={(e) => handleAddressChange('line', e.target.value)}
                placeholder="123 Main St"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  placeholder="City"
                />
              </div>
              
              <div>
                <Label htmlFor="addressState">State</Label>
                <Input
                  id="addressState"
                  value={formData.address.state}
                  onChange={(e) => handleAddressChange('state', e.target.value)}
                  placeholder="State"
                />
              </div>
              
              <div>
                <Label htmlFor="zip">ZIP Code</Label>
                <Input
                  id="zip"
                  value={formData.address.zip}
                  onChange={(e) => handleAddressChange('zip', e.target.value)}
                  placeholder="12345"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || !hasChanges}>
            {isLoading ? 'Saving...' : (isNewLicense ? 'Add License' : 'Save Changes')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

